import { Component, Input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AvatarService } from '../../services/avatar.service';

@Component({
  selector: 'app-avatar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.css']
})
export class AvatarComponent implements OnInit {
  /**
   * User's name for generating initials
   */
  @Input() name?: string;

  /**
   * Avatar image URL
   */
  @Input() avatarUrl?: string | null;

  /**
   * User ID for consistent color generation
   */
  @Input() userId?: string;

  /**
   * Size variant
   */
  @Input() size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' = 'md';

  /**
   * Alt text for accessibility
   */
  @Input() alt?: string;

  /**
   * Additional CSS classes
   */
  @Input() customClass?: string;

  /**
   * Show loading skeleton
   */
  @Input() loading: boolean = false;

  /**
   * Border style
   */
  @Input() border: boolean = false;

  /**
   * Border color (Tailwind class)
   */
  @Input() borderColor: string = 'border-white';

  /**
   * Border width (Tailwind class)
   */
  @Input() borderWidth: string = 'border-2';

  // Internal state
  initials = signal<string>('');
  backgroundColor = signal<string>('');
  textColor = signal<string>('');
  sizeClasses = signal<string>('');
  showImage = signal<boolean>(false);
  imageError = signal<boolean>(false);

  constructor(private avatarService: AvatarService) {}

  ngOnInit(): void {
    this.initializeAvatar();
  }

  ngOnChanges(): void {
    this.initializeAvatar();
  }

  private initializeAvatar(): void {
    // Get initials
    this.initials.set(this.avatarService.getInitials(this.name));

    // Get background color based on name or userId
    const identifier = this.name || this.userId || 'User';
    const bgColor = this.avatarService.getBackgroundColor(identifier);
    this.backgroundColor.set(bgColor);

    // Get text color for contrast
    this.textColor.set(this.avatarService.getTextColor(bgColor));

    // Get size classes
    this.sizeClasses.set(this.avatarService.getSizeClasses(this.size));

    // Check if we should show image
    const validUrl = this.avatarService.getAvatarUrl(this.avatarUrl, this.userId);
    this.showImage.set(!!validUrl && !this.imageError());
  }

  /**
   * Handle image load error
   */
  onImageError(): void {
    this.imageError.set(true);
    this.showImage.set(false);
  }

  /**
   * Handle image load success
   */
  onImageLoad(): void {
    this.imageError.set(false);
    this.showImage.set(true);
  }

  /**
   * Get the final avatar URL to display
   */
  get finalAvatarUrl(): string | null {
    return this.avatarService.getAvatarUrl(this.avatarUrl, this.userId);
  }

  /**
   * Get alt text for the avatar
   */
  get altText(): string {
    return this.alt || `${this.name || 'User'} avatar`;
  }

  /**
   * Get border classes
   */
  get borderClasses(): string {
    if (!this.border) return '';
    return `${this.borderWidth} ${this.borderColor}`;
  }
}

